export const HOME_CHAT_SUGGESTION_COUNT = 2;
export const MAX_HOME_PERSONAL_SUGGESTIONS = 12;
const HOME_PERSONAL_SUGGESTION_FALLBACK_EMOJI = '✨';

export const compactHomeChatSuggestion = (value) => {
    if (typeof value !== 'string') {
        return '';
    }

    return value.trim().replace(/\s+/gu, ' ');
};

export const normalizeHomeChatSuggestion = (value) => {
    const compacted = compactHomeChatSuggestion(value)
        .normalize('NFKC')
        .toLowerCase();
    const normalized = compacted
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .replace(/\s+/gu, ' ')
        .trim();

    return normalized || compacted;
};

export const sanitizeHomePersonalSuggestions = (suggestions) => {
    const sanitized = [];
    const usedKeys = new Set();

    for (const value of Array.isArray(suggestions) ? suggestions : []) {
        const suggestion = value && typeof value === 'object'
            ? value
            : { text: value };
        const text = compactHomeChatSuggestion(suggestion.text);
        const normalizedText = normalizeHomeChatSuggestion(text);
        if (!normalizedText || usedKeys.has(normalizedText)) {
            continue;
        }

        usedKeys.add(normalizedText);
        const emoji = typeof suggestion.emoji === 'string'
            ? suggestion.emoji.trim()
            : '';
        sanitized.push({
            text,
            emoji: emoji || HOME_PERSONAL_SUGGESTION_FALLBACK_EMOJI,
        });
        if (sanitized.length === MAX_HOME_PERSONAL_SUGGESTIONS) {
            break;
        }
    }

    return sanitized;
};

export const sanitizeDismissedPublicExampleKeys = (keys) => {
    const sanitized = [];
    const seen = new Set();

    for (const value of Array.isArray(keys) ? keys : []) {
        const key = typeof value === 'string' ? value.trim() : '';
        if (!key || seen.has(key)) {
            continue;
        }

        seen.add(key);
        sanitized.push(key);
    }

    return sanitized;
};

export const resolveDismissedPublicExampleKeys = (response, fallback = []) => {
    if (
        !response ||
        typeof response !== 'object' ||
        !Object.prototype.hasOwnProperty.call(response, 'dismissedPublicExampleKeys')
    ) {
        return sanitizeDismissedPublicExampleKeys(fallback);
    }

    return sanitizeDismissedPublicExampleKeys(response.dismissedPublicExampleKeys);
};

export const resolveHomePersonalSuggestions = (response) => {
    if (!response || typeof response !== 'object') {
        return [];
    }

    const suggestionItems = Array.isArray(response.suggestionItems)
        ? response.suggestionItems
        : [];
    const legacySuggestions = Array.isArray(response.suggestions)
        ? response.suggestions
        : [];
    if (!legacySuggestions.length) {
        return sanitizeHomePersonalSuggestions(suggestionItems);
    }

    const alignedSuggestions = legacySuggestions.map((legacySuggestion, index) => {
        const suggestionItem = suggestionItems[index];
        if (
            suggestionItem &&
            typeof suggestionItem === 'object' &&
            compactHomeChatSuggestion(suggestionItem.text) ===
                compactHomeChatSuggestion(legacySuggestion)
        ) {
            return suggestionItem;
        }

        return legacySuggestion;
    });

    return sanitizeHomePersonalSuggestions(alignedSuggestions);
};

const buildDefaultRows = (defaults) => {
    const rows = [];
    const usedKeys = new Set();

    for (const example of Array.isArray(defaults) ? defaults : []) {
        if (!example || typeof example !== 'object') {
            continue;
        }

        const publicExampleKey = typeof example.key === 'string' ? example.key.trim() : '';
        const threadId = typeof example.threadId === 'string' ? example.threadId.trim() : '';
        const text = compactHomeChatSuggestion(example.label);
        const normalizedText = normalizeHomeChatSuggestion(text);
        if (!publicExampleKey || !threadId || !normalizedText || usedKeys.has(normalizedText)) {
            continue;
        }

        usedKeys.add(normalizedText);
        rows.push({
            kind: 'default',
            publicExampleKey,
            publicExamplesRevision: typeof example.publicExamplesRevision === 'string'
                ? example.publicExamplesRevision.trim()
                : '',
            threadId,
            emoji: typeof example.emoji === 'string' ? example.emoji : '',
            text,
        });
    }

    return rows;
};

const availableDefaultRows = ({
    defaults,
    dismissedPublicExampleKeys,
    personalSuggestions,
}) => {
    const personalKeys = new Set(
        sanitizeHomePersonalSuggestions(personalSuggestions).map((suggestion) => (
            normalizeHomeChatSuggestion(suggestion.text)
        )),
    );
    const dismissedKeys = new Set(
        sanitizeDismissedPublicExampleKeys(dismissedPublicExampleKeys),
    );

    return buildDefaultRows(defaults).filter((suggestion) => (
        !dismissedKeys.has(suggestion.publicExampleKey) &&
        !personalKeys.has(normalizeHomeChatSuggestion(suggestion.text))
    ));
};

const normalizedOffset = (offset, size) => {
    if (size === 0) {
        return 0;
    }

    const numericOffset = Number.isFinite(offset) ? Math.trunc(offset) : 0;
    return ((numericOffset % size) + size) % size;
};

const rotatedTake = (values, offset, count) => {
    if (!values.length || count <= 0) {
        return [];
    }

    const start = normalizedOffset(offset, values.length);
    return Array.from(
        { length: Math.min(count, values.length) },
        (_, index) => values[(start + index) % values.length],
    );
};

const rotatedFind = (values, offset, predicate) => {
    if (!values.length) {
        return null;
    }

    const start = normalizedOffset(offset, values.length);
    for (let distance = 0; distance < values.length; distance += 1) {
        const value = values[(start + distance) % values.length];
        if (predicate(value)) {
            return value;
        }
    }

    return null;
};

const nextOffset = (values, offset, selectedCount) => {
    if (!values.length) {
        return 0;
    }

    return (normalizedOffset(offset, values.length) + selectedCount) % values.length;
};

export const selectHomeChatSuggestions = ({
    personalSuggestions = [],
    defaults = [],
    dismissedPublicExampleKeys = [],
    personalOffset = 0,
    defaultOffset = 0,
} = {}) => {
    const sanitizedPersonal = sanitizeHomePersonalSuggestions(personalSuggestions);
    const personalRows = sanitizedPersonal.map((suggestion) => ({
        kind: 'personal',
        emoji: suggestion.emoji,
        text: suggestion.text,
    }));
    const defaultRows = availableDefaultRows({
        defaults,
        dismissedPublicExampleKeys,
        personalSuggestions: sanitizedPersonal,
    });
    const selectedPersonal = rotatedTake(
        personalRows,
        personalOffset,
        HOME_CHAT_SUGGESTION_COUNT,
    );
    const selectedDefaults = rotatedTake(
        defaultRows,
        defaultOffset,
        HOME_CHAT_SUGGESTION_COUNT - selectedPersonal.length,
    );

    return {
        suggestions: [...selectedPersonal, ...selectedDefaults],
        nextPersonalOffset: nextOffset(personalRows, personalOffset, selectedPersonal.length),
        nextDefaultOffset: nextOffset(defaultRows, defaultOffset, selectedDefaults.length),
    };
};

export const removeMatchingHomePersonalSuggestion = (suggestions, executedQuery) => {
    const executedKey = normalizeHomeChatSuggestion(executedQuery);
    const sanitized = sanitizeHomePersonalSuggestions(suggestions);
    if (!executedKey) {
        return sanitized;
    }

    return sanitized.filter((suggestion) => (
        normalizeHomeChatSuggestion(suggestion.text) !== executedKey
    ));
};

export const preservedHomePersonalSuggestionOffset = ({
    previousSuggestions = [],
    nextSuggestions = [],
    previousOffset = 0,
} = {}) => {
    const previous = sanitizeHomePersonalSuggestions(previousSuggestions);
    const next = sanitizeHomePersonalSuggestions(nextSuggestions);
    if (!next.length) {
        return 0;
    }
    if (!previous.length) {
        return normalizedOffset(previousOffset, next.length);
    }

    const nextIndices = new Map(
        next.map((suggestion, index) => [
            normalizeHomeChatSuggestion(suggestion.text),
            index,
        ]),
    );
    const previousStart = normalizedOffset(previousOffset, previous.length);
    for (let distance = 0; distance < previous.length; distance += 1) {
        const previousSuggestion = previous[(previousStart + distance) % previous.length];
        const nextIndex = nextIndices.get(
            normalizeHomeChatSuggestion(previousSuggestion.text),
        );
        if (nextIndex !== undefined) {
            return nextIndex;
        }
    }

    return normalizedOffset(previousOffset, next.length);
};

export const preservedHomeDefaultSuggestionOffset = ({
    previousPersonalSuggestions = [],
    nextPersonalSuggestions = previousPersonalSuggestions,
    defaults = [],
    previousDismissedPublicExampleKeys = [],
    nextDismissedPublicExampleKeys = [],
    previousOffset = 0,
} = {}) => {
    const previous = availableDefaultRows({
        defaults,
        dismissedPublicExampleKeys: previousDismissedPublicExampleKeys,
        personalSuggestions: previousPersonalSuggestions,
    });
    const next = availableDefaultRows({
        defaults,
        dismissedPublicExampleKeys: nextDismissedPublicExampleKeys,
        personalSuggestions: nextPersonalSuggestions,
    });
    if (!next.length) {
        return 0;
    }
    if (!previous.length) {
        return normalizedOffset(previousOffset, next.length);
    }

    const nextIndices = new Map(
        next.map((suggestion, index) => [suggestion.publicExampleKey, index]),
    );
    const previousStart = normalizedOffset(previousOffset, previous.length);
    for (let distance = 0; distance < previous.length; distance += 1) {
        const previousSuggestion = previous[(previousStart + distance) % previous.length];
        const nextIndex = nextIndices.get(previousSuggestion.publicExampleKey);
        if (nextIndex !== undefined) {
            return nextIndex;
        }
    }

    return normalizedOffset(previousOffset, next.length);
};

export const homeSuggestionOffsetsAfterInsertingReplacement = ({
    personalSuggestions = [],
    insertedSuggestion = null,
    personalOffset = 0,
    defaultOffset = 0,
    defaults = [],
    dismissedPublicExampleKeys = [],
} = {}) => {
    if (!insertedSuggestion) {
        return { personalOffset, defaultOffset };
    }

    const insertedKey = normalizeHomeChatSuggestion(insertedSuggestion.text);
    if (insertedSuggestion.kind === 'personal') {
        const personal = sanitizeHomePersonalSuggestions(personalSuggestions);
        const insertedIndex = personal.findIndex((suggestion) => (
            normalizeHomeChatSuggestion(suggestion.text) === insertedKey
        ));
        return {
            personalOffset: insertedIndex >= 0
                ? (insertedIndex + 1) % personal.length
                : personalOffset,
            defaultOffset,
        };
    }

    const availableDefaults = availableDefaultRows({
        defaults,
        dismissedPublicExampleKeys,
        personalSuggestions,
    });
    const insertedIndex = availableDefaults.findIndex((suggestion) => (
        normalizeHomeChatSuggestion(suggestion.text) === insertedKey
    ));
    return {
        personalOffset,
        defaultOffset: insertedIndex >= 0
            ? (insertedIndex + 1) % availableDefaults.length
            : defaultOffset,
    };
};

export const homeChatSuggestionIdentity = (suggestion) => {
    if (!suggestion || typeof suggestion !== 'object') {
        return '';
    }
    if (suggestion.kind === 'default') {
        const publicExampleKey = typeof suggestion.publicExampleKey === 'string'
            ? suggestion.publicExampleKey.trim()
            : '';
        return publicExampleKey ? `default:${publicExampleKey}` : '';
    }
    if (suggestion.kind === 'personal') {
        const normalizedText = normalizeHomeChatSuggestion(suggestion.text);
        return normalizedText ? `personal:${normalizedText}` : '';
    }
    return '';
};

export const replaceDismissedHomeSuggestion = ({
    visibleSuggestions = [],
    dismissedSuggestion,
    personalSuggestions = [],
    defaults = [],
    dismissedPublicExampleKeys = [],
    personalOffset = 0,
    defaultOffset = 0,
} = {}) => {
    const dismissedIdentity = homeChatSuggestionIdentity(dismissedSuggestion);
    if (!dismissedIdentity) {
        return visibleSuggestions;
    }

    const dismissedKey = normalizeHomeChatSuggestion(dismissedSuggestion.text);
    const dismissedIndex = visibleSuggestions.findIndex((suggestion) => (
        suggestion === dismissedSuggestion ||
        homeChatSuggestionIdentity(suggestion) === dismissedIdentity
    ));
    if (dismissedIndex < 0) {
        return visibleSuggestions;
    }

    const nextSuggestions = visibleSuggestions.filter((_, index) => index !== dismissedIndex);
    const unavailableKeys = new Set(
        nextSuggestions.map((suggestion) => normalizeHomeChatSuggestion(suggestion && suggestion.text)),
    );
    unavailableKeys.add(dismissedKey);

    const sanitizedPersonal = sanitizeHomePersonalSuggestions(personalSuggestions);
    const personalReplacementSuggestion = rotatedFind(
        sanitizedPersonal,
        personalOffset,
        (suggestion) => (
            !unavailableKeys.has(normalizeHomeChatSuggestion(suggestion.text))
        ),
    );
    const personalReplacement = personalReplacementSuggestion ? {
        kind: 'personal',
        emoji: personalReplacementSuggestion.emoji,
        text: personalReplacementSuggestion.text,
    } : null;
    const effectiveDismissedPublicExampleKeys = dismissedSuggestion.kind === 'default'
        ? [...dismissedPublicExampleKeys, dismissedSuggestion.publicExampleKey]
        : dismissedPublicExampleKeys;
    const defaultReplacement = personalReplacement ? null : rotatedFind(
        availableDefaultRows({
            defaults,
            dismissedPublicExampleKeys: effectiveDismissedPublicExampleKeys,
            personalSuggestions: sanitizedPersonal,
        }),
        defaultOffset,
        (suggestion) => !unavailableKeys.has(normalizeHomeChatSuggestion(suggestion.text)),
    );
    const replacement = personalReplacement || defaultReplacement;

    if (replacement) {
        nextSuggestions.splice(dismissedIndex, 0, replacement);
    }

    return nextSuggestions;
};
